import { useContext, useEffect, useRef } from "react";

import { saveItineraryDetailsAPI } from "../api/itinerary/saveItineraryDetails";
import { StateContext } from "../context/globalContext/context";
import { iGlobalContext } from "../context/globalContext/interface";
import { useSocketContext, iSocketContext } from "../context/socketContext/SocketContext";
import { consoleLog } from "../utils/consoleLog/consoleLog";
import { iChatData } from "../types";

// One auto-POST per chat-bubble id. Tracks which selectedChatData ids we've
// already shipped so the effect doesn't re-fire on every re-render or when
// the user scrolls/refines and the same id re-surfaces.
const sentIds = new Set<string>();

// Auto-saves a freshly-streamed itinerary into the user's itineraries table
// the moment streaming completes, so:
//   1. The user doesn't have to click "Save" before sharing — the share
//      URL `/shared/<selectedChatData.id>` resolves immediately via
//      getItineraryDetailsAPI on the recipient's side.
//   2. The trip shows up in the user's saved trips list right away.
//
// Skipped when:
//   - On /shared/:id (the clone-on-interaction flow owns that path).
//   - Still streaming.
//   - Trip already saved (selectedChatData.isSaved === true) or already
//     POSTed under this id (sentIds set).
//   - destinationList empty (nothing worth saving yet).
//   - User not logged in.
//
// Stamps `isSaved: true` and mirrors the field onto the matching chatData
// entry so subsequent renders don't re-trigger the effect after id change.
export function useAutoSaveItinerary() {
  const state = useContext(StateContext);
  const {
    selectedChatData,
    setSelectedChatData,
    setChatData,
    accessJWT,
    userInfo,
  }: iGlobalContext = state;
  const { streamingFlagAndResponseType }: iSocketContext = useSocketContext();
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (inFlightRef.current) return;
    if (!userInfo?.id || !accessJWT) return;
    // Skip ONLY while actively streaming; a finished stream (false) or no
    // stream at all (undefined — saved-trip load) should both proceed.
    if (streamingFlagAndResponseType?.isStillStreamingFlag === true) return;
    // Skip on the public viewer page — the clone-on-interaction flow
    // (cloneSharedTravelPlanIfNeeded) handles persisting the trip into
    // the viewer's account.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/shared/")) return;

    const chatId = selectedChatData?.id;
    if (!chatId) return;
    // Already saved or already POSTed under this id — skip.
    if (selectedChatData?.isSaved) return;
    if (sentIds.has(chatId)) return;
    // Need a non-empty itinerary to be worth saving.
    if (!Array.isArray(selectedChatData?.destinationList) || selectedChatData.destinationList.length === 0)
      return;
    // Only auto-save TRUE chat-streamed itineraries. Direct/Shop-Travel
    // bookings populate selectedChatData with a placeholder (just an `id` and
    // a single `destinationList` entry carrying an imageURL) — none of the
    // LLM-streamed fields (`results`, `complete_itinerary_description`,
    // `city_wise_itinerary`, `messages`) are present. Auto-saving those
    // placeholders produced a duplicate POST to `/app/v1/users/itineraries`
    // right before prebook's own `createItineraryAPI` POST. The real save
    // for direct bookings happens during prebook (see
    // TravelerCrumbsDetailsContext.createItineraryAPI), so this hook should
    // sit it out for non-chat flows.
    const cd: any = selectedChatData;
    const hasChatContent = !!(
      cd?.results ||
      cd?.complete_itinerary_description ||
      (Array.isArray(cd?.city_wise_itinerary) && cd.city_wise_itinerary.length > 0) ||
      (Array.isArray(cd?.messages) && cd.messages.length > 0)
    );
    if (!hasChatContent) return;

    sentIds.add(chatId);
    inFlightRef.current = true;
    (async () => {
      try {
        const payload = {
          tripName: selectedChatData.tripName || "",
          userId: userInfo.id,
          metaData: {
            selectedChatData: {
              ...selectedChatData,
              isSaved: true,
              prompt: [],
              postText: "",
              preText: "",
            },
          },
          status: "draft",
          numberOfDaysSelected: Number(selectedChatData.noOfDays || selectedChatData.number_of_days || 0),
          year: "",
          selectedMonth: "",
          is_admin_created: userInfo?.role === 1,
          data: {},
          // selectedChatData.id is a UUID generated by v4() on the chat
          // bubble; passing it as `id` makes TypeORM use it as the primary
          // key on insert (the manual save flow does the same — keeps
          // selectedChatData.id === Itinerary.id invariant).
          id: chatId,
          historyId: "",
        };
        consoleLog(["AUTO SAVE ITINERARY PAYLOAD", payload]);
        const response: any = await saveItineraryDetailsAPI(accessJWT, payload, "POST");
        if (response?.message !== "Itineraries has been saved") {
          consoleLog(["AUTO SAVE ITINERARY — failed", response]);
          // Allow a retry on the next streaming-complete tick if this
          // didn't actually persist.
          sentIds.delete(chatId);
          return;
        }
        consoleLog(["AUTO SAVE ITINERARY — saved", { chatId }]);
        // Mark in-memory state as saved so the manual Save button hides
        // and subsequent renders don't re-trigger the effect.
        setSelectedChatData((prev: any) => {
          if (!prev || prev.id !== chatId) return prev;
          return { ...prev, isSaved: true };
        });
        // Mirror onto the matching chatData entry (and any nested
        // destinationList entry, mirroring useHandleSaveItineraryDetails)
        // so re-derivation of selectedChatData from chatData preserves
        // the flag.
        setChatData?.((prev: iChatData[]) => {
          if (!Array.isArray(prev)) return prev;
          let mutated = false;
          const next = prev.map((entry: any) => {
            if (entry?.id === chatId && !entry?.isSaved) {
              mutated = true;
              return { ...entry, isSaved: true };
            }
            if (Array.isArray(entry?.destinationList)) {
              let nestedMutated = false;
              const nextList = entry.destinationList.map((d: any) => {
                if (d?.id === chatId && !d?.isSaved) {
                  nestedMutated = true;
                  return { ...d, isSaved: true };
                }
                return d;
              });
              if (nestedMutated) {
                mutated = true;
                return { ...entry, destinationList: nextList };
              }
            }
            return entry;
          });
          return mutated ? next : prev;
        });
      } finally {
        inFlightRef.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accessJWT,
    userInfo?.id,
    streamingFlagAndResponseType?.isStillStreamingFlag,
    selectedChatData?.id,
    selectedChatData?.isSaved,
    selectedChatData?.destinationList?.length,
  ]);
}
